// Pure calculations behind the Executive Brief and the Project Cockpit. No React, no Supabase.
// Same rule as pmoSummary: a figure that is not recorded is null, never a zero that reads as good news.

export interface PendingDecision {
  id: string;
  title: string;
  project_name: string | null;
  due_date: string | null;
  priority: string | null;
  amount: number | null;
}

const ms = (d: string | null) => (d ? new Date(d).getTime() : Number.POSITIVE_INFINITY);
export const isOverdue = (due: string | null, now: Date) => !!due && new Date(due).getTime() < now.getTime();

/** Overdue first, then nearest due date, undated last. Stable. */
export function decisionQueue<T extends { due_date: string | null }>(decisions: T[], now: Date = new Date()): T[] {
  return decisions
    .map((d, i) => ({ d, i }))
    .sort((a, b) => Number(isOverdue(b.d.due_date, now)) - Number(isOverdue(a.d.due_date, now)) || ms(a.d.due_date) - ms(b.d.due_date) || a.i - b.i)
    .map((x) => x.d);
}

export function decisionStats(decisions: PendingDecision[], now: Date = new Date()) {
  const amounts = decisions.map((d) => d.amount).filter((a): a is number => typeof a === 'number' && a > 0);
  return {
    total: decisions.length,
    overdue: decisions.filter((d) => isOverdue(d.due_date, now)).length,
    /** Sum of recorded amounts; null when no pending decision has an amount. */
    amount: amounts.length ? amounts.reduce((s, a) => s + a, 0) : null,
  };
}

export interface Pillar {
  id: string;
  name: string;
  status: string | null;
  progress_pct: number | null;
  target_pct: number | null;
}

export function pillarStats(pillars: Pillar[]) {
  const measured = pillars.filter((p) => typeof p.progress_pct === 'number');
  return {
    total: pillars.length,
    /** Average progress of pillars that have one recorded; null when none do. */
    avgProgress: measured.length ? Math.round(measured.reduce((s, p) => s + (p.progress_pct as number), 0) / measured.length) : null,
    onTarget: pillars.filter((p) => typeof p.progress_pct === 'number' && typeof p.target_pct === 'number' && p.progress_pct >= p.target_pct).length,
  };
}

export interface CockpitTask {
  id: string;
  external_id: string | null;
  title: string;
  status: string | null;
  priority: string | null;
  assignee: string | null;
  due_date: string | null;
  project_external_id: string | null;
}

const norm = (s: string | null | undefined) => (s ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
/** Synced tasks carry the assignee as a display name, so "mine" is a case- and spacing-insensitive name match. */
export const sameName = (a: string | null | undefined, b: string | null | undefined) => !!norm(a) && norm(a) === norm(b);

export function cockpitSummary(tasks: CockpitTask[], myName: string | null | undefined, now: Date = new Date()) {
  const open = tasks.filter((t) => t.status !== 'done');
  const byDue = (a: CockpitTask, b: CockpitTask) => ms(a.due_date) - ms(b.due_date);
  return {
    blocked: open.filter((t) => t.status === 'blocked').sort(byDue),
    /** Past due and not done, oldest first. Blocked tasks that are also late appear here too. */
    overdue: open.filter((t) => isOverdue(t.due_date, now)).sort(byDue),
    mine: open.filter((t) => sameName(t.assignee, myName)).sort(byDue),
  };
}

// ---- My Day (user), Program Board (program manager), Workspace Health (admin) ----

export interface ActionItem {
  id: string;
  title: string;
  assignee_id: string | null;
  assignee_name: string | null;
  due_date: string | null;
  status: string | null;
}

const CLOSED_ACTION = new Set(['done', 'completed', 'rejected', 'dismissed', 'cancelled']);

/** Open action items assigned to this person: by user id when recorded, otherwise by display-name match. */
export function myActionItems(items: ActionItem[], userId: string | null | undefined, myName: string | null | undefined): ActionItem[] {
  return items
    .filter((i) => !CLOSED_ACTION.has((i.status ?? '').toLowerCase()))
    .filter((i) => (!!userId && i.assignee_id === userId) || sameName(i.assignee_name, myName))
    .sort((a, b) => ms(a.due_date) - ms(b.due_date));
}

/** Items due between now and `days` from now (already-overdue items are not "due soon"). */
export function dueWithin<T extends { due_date: string | null }>(items: T[], days: number, now: Date = new Date()): T[] {
  const end = now.getTime() + days * 86_400_000;
  return items.filter((i) => !!i.due_date && new Date(i.due_date).getTime() >= now.getTime() && new Date(i.due_date).getTime() <= end);
}

export interface RiskLite { id: string; entity_type: string; entity_id: string; rule_id: string; severity: string; detected_at: string; }

export function riskBreakdown(risks: RiskLite[]) {
  return {
    open: risks.length,
    highOrCritical: risks.filter((r) => r.severity === 'high' || r.severity === 'critical').length,
    /** Signals raised by the blocked-dependency rule: work waiting on other work. */
    dependencies: risks.filter((r) => r.rule_id === 'blocked_dependency'),
  };
}

export interface SourceLite { id: string; name: string; status: 'ok' | 'warn' | 'off'; lastSyncAt: string | null; }

export function sourceHealth(sources: SourceLite[]) {
  return {
    total: sources.length,
    healthy: sources.filter((s) => s.status === 'ok').length,
    stale: sources.filter((s) => s.status === 'warn').length,
    paused: sources.filter((s) => s.status === 'off').length,
  };
}

export interface SyncLog { id: string; integration_id: string; status: string; started_at: string; error_message: string | null; }

/** Failed sync runs within the last `hours`, newest first. */
export function failedSyncs(logs: SyncLog[], now: Date = new Date(), hours = 24): SyncLog[] {
  const cutoff = now.getTime() - hours * 3_600_000;
  return logs
    .filter((l) => l.status === 'failed' && new Date(l.started_at).getTime() >= cutoff)
    .sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime());
}
